import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";

import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";

import { Status, StatusChangeHistory, StatusMenu, User } from "@common/models";
import { AuthenticationService, StatusChangeHistoryService, StatusMenuService, UserService } from "@common/services";
import { InsightsService, MSGraphService } from "@core/services";
import { blobToBase64, distinct } from "@core/utils";

import { CustomSwalService } from "app/helpers";
import { environment } from "environments/environment";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, filter, take, takeUntil } from "rxjs/operators";

@Component({
    selector: "app-timeline",
    templateUrl: "./timeline.component.html",
    styleUrls: ['./timeline.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TimelineComponent implements OnInit, OnDestroy {

    // Inputs
    @Input() menuId: number;
    @Input() objectId: number;
    // Nombre del objeto, va de subtítulo (es un dato, no se traduce)
    @Input() title: string;
    @Input() object: any;

    // Variables publicas
    public statusList: Status[] = [];
    public approvalList: any[] = [];
    public message: string;
    // Fotos de Entra por entraReferenceId (mismo patrón que users-list)
    public photos: { [entraReferenceId: string]: string } = {};

    // Privado
    private createdUser: User;
    private _unsubscribeAll: Subject<any>;

    constructor(
        public activeModal: NgbActiveModal,
        private _customSwalService: CustomSwalService,
        private _statusMenuService: StatusMenuService,
        private _statusChangeHistoryService: StatusChangeHistoryService,
        private _userService: UserService,
        private _translateService: TranslateService,
        private _insightsService: InsightsService,
        private _authenticationService: AuthenticationService,
        private cdf: ChangeDetectorRef,
        private _msGraphService: MSGraphService
    ) {
        this._unsubscribeAll = new Subject();
     }

    ngOnInit() {
        this.log('Abrió la línea de tiempo', this.logData());

        this._customSwalService.showLoading();

        // El usuario creador y los estados del menú se piden juntos: el primer
        // paso de la línea usa al creador, así que tiene que estar antes de
        // armarla. Si el usuario falla, la línea se arma igual sin él (sin
        // alerta: el close() del loading la cerraba al instante igual).
        const createdUser$ = this._userService.getById(this.object?.createdUserId).pipe(
            catchError(err => {
                this.log('Error al obtener el usuario creador para la línea de tiempo', this.logData(), err, true);
                return of(null);
            })
        );

        forkJoin([createdUser$, this._statusMenuService.getByMenuId(this.menuId)])
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(
            ([user, resp]: [User, StatusMenu[]]) => {
                this.createdUser = user;

                // Obtiene la lista de estados que no tengan padre
                this.statusList = resp.filter(sm => !sm.id.status.parent)
                .map(sm => sm.id.status)
                .filter(distinct)
                .sort((a, b) => a.code.localeCompare(b.code));

                // Obtiene el historico de aprobaciones
                this.getApprovals(this.menuId, this.objectId);
            },
            err => {
                this.log('Error al obtener los estados del menú para la línea de tiempo', this.logData(), err, true);
                this.showMessage('COMMON.TIME_LINE.MESSAGE.HISTORY_REQUEST_ERROR');
            }
        );
    }


    /**
     * On Destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscription
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Cierra el modal (X y botón Cerrar)
     */
    close(): void {
        this.log('Cerró la línea de tiempo', this.logData());
        this.activeModal.close('Cross click');
    }

    /**
     * El paso actual es el estado (o el padre del estado) en el que está el objeto
     * @param item Paso de la línea de tiempo
     */
    isCurrent(item: any): boolean {
        const id = item?.status?.id;
        return id != null && (this.object?.legalStatus?.parent?.id === id || this.object?.status?.parent?.id === id);
    }

    /**
     * El tramo de línea que sale de este paso se pinta si el recorrido siguió
     * más allá: algún paso posterior tiene fecha o es el actual. Así quedan
     * unidos los pasos que sí pasaron, aunque haya estados salteados en medio.
     * @param index Posición del paso
     */
    isLinked(index: number): boolean {
        return this.approvalList.slice(index + 1).some(item => !!item.createdAt || this.isCurrent(item));
    }

    /**
     * Paso salteado: no ocurrió (sin fecha, no es el actual), pero el recorrido
     * siguió más allá; la línea pasa por él y el punto lo marca como saltado
     * @param index Posición del paso
     */
    isSkipped(index: number): boolean {
        const item = this.approvalList[index];
        return !item?.createdAt && !this.isCurrent(item) && this.isLinked(index);
    }

    /**
     * Nombre completo; algunos usuarios no tienen persona asociada
     * @param user Usuario del paso
     */
    fullName(user: any): string {
        const source = user?.person ?? user;
        return `${source?.name ?? ''} ${source?.surname ?? ''}`.trim();
    }

    /**
     * Iniciales para el avatar
     * @param user Usuario del paso
     */
    initials(user: any): string {
        const source = user?.person ?? user;
        return `${(source?.name ?? '').charAt(0)}${(source?.surname ?? '').charAt(0)}`.toUpperCase();
    }

    /**
     * Carga el historico de aprobaciones o de estados
     * @param {number} menuId Indica el id del menú
     * @param {number} objectId Indica el id del objeto que se va a buscar el historico
     */
    private getApprovals(menuId: number, objectId: number){
        // Obtiene el historico de aprobaciones
        this._statusChangeHistoryService.getByMenuIdAndObjectId(menuId, objectId)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(
            (res: StatusChangeHistory[]) => {

                // Si la respuesta del historico de cambios viene vacía
                if (!res || res.length == 0){
                    this.showMessage('COMMON.TIME_LINE.MESSAGE.HISTORY_NOT_FOUND', false);
                }

                // Verificamos que tenga objetos
                this.statusList.forEach((status: Status, index: number) => {

                    // Obtiene el último historico que encuentre para el estado
                    let lastHistory = res?.filter(history => status.id === (history.newStatus?.parent?.id ?? history.newStatus?.id))
                        .sort((a,b) => a.createdAt.getTime() - b.createdAt.getTime());

                    // Si no encuentra un último historico y el index es igual a 0
                    // Entonces crea el del primer estado cuando se creó el objeto
                    if ((!lastHistory || lastHistory.length == 0) && index == 0){
                         // Crea un estado de aprobaciones con el estado y la fecha de creación
                         this.approvalList.push(
                            {
                                status: status,
                                createdAt:this.object.createdAt,
                                createdUser: this.createdUser
                            }
                        );
                    } else {
                        // Sino agrega un estado de aprobaciones con el historico que encontró
                        this.approvalList.push(
                            {
                                status: status,
                                createdAt: (lastHistory && lastHistory.length > 0) ? lastHistory[0].createdAt : undefined,
                                createdUser:(lastHistory && lastHistory.length > 0) ? lastHistory[0].createdUser : undefined
                            }
                        );
                    }
                });

                // Ordena el listado de aprobaciones
                this.approvalList = this.approvalList
                .sort((a, b) => a.status.code.localeCompare(b.status.code));

                // Fotos de quienes aparecen en la línea
                this.loadProfilePhotos(this.approvalList.map(item => item.createdUser));

                // Cierra el swal alert
                this._customSwalService.close();
                this.cdf.detectChanges();
            },
            (err) => {
                this.log('Error al obtener el historial para la línea de tiempo', this.logData(), err, true);
                this.showMessage('COMMON.TIME_LINE.MESSAGE.HISTORY_REQUEST_ERROR');
            }
        );
    }

    /**
     * Pide a Microsoft Graph la foto de los usuarios de la línea que tengan
     * cuenta de Entra (mismo patrón que users-list): se espera el token de
     * Graph y el blob se pasa a base64 para bindearlo al <img>. Sin foto, el
     * avatar local y, en último caso, las iniciales.
     * @param users Usuarios de los pasos (puede haber repetidos o vacíos)
     */
    private loadProfilePhotos(users: User[]): void {
        const pending = Array.from(new Set((users || [])
            .map(user => user?.entraReferenceId)
            .filter(id => !!id && !this.photos[id])));

        if (!pending.length) return;

        // Si la autenticación es por medio del active directory
        if (localStorage.getItem('auth-type') === 'saml') {
            this._msGraphService.register();
        }

        this._msGraphService.onAccessTokenChanged
            .pipe(filter(token => !!token), take(1), takeUntil(this._unsubscribeAll))
            .subscribe(() => pending.forEach(id => this.loadProfilePhoto(id)));
    }

    /**
     * Foto de un usuario puntual; si Graph responde con error (lo normal
     * cuando la persona no subió foto) queda el avatar por defecto
     * @param entraReferenceId Id del usuario en Microsoft 365
     */
    private loadProfilePhoto(entraReferenceId: string): void {
        this._msGraphService.getPhotoByUserId(entraReferenceId)
            .pipe(catchError(() => of(null)), takeUntil(this._unsubscribeAll))
            .subscribe(resp => {
                if (!resp) return;

                blobToBase64(resp).then(value => {
                    Promise.resolve().then(() => {
                        this.photos[entraReferenceId] = value as string;
                        this.cdf.detectChanges();
                    });
                });
            });
    }

    /**
     * Muestra un aviso dentro del modal y cierra el loading (antes, en los
     * errores, el loading quedaba abierto encima del modal)
     * @param key Clave de traducción del aviso
     * @param closeLoading Si además hay que cerrar el loading
     */
    private showMessage(key: string, closeLoading: boolean = true): void {
        if (closeLoading) {
            this._customSwalService.close();
        }
        this._translateService.get([key]).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
            this.message = value[key];
        });
    }

    /**
     * Contexto para las trazas: ids, no el objeto
     */
    private logData(): any {
        return {
            menuId: this.menuId ?? null,
            objectId: this.objectId ?? null,
        };
    }

    /**
     * Manda una traza a Application Insights, etiquetada con el proyecto y el
     * usuario que la originó.
     */
    private log(message: string, data: any, ex: any = null, error: boolean = false): void {
        const user = this._authenticationService.currentUserValue;

        if (error) {
            this._insightsService.traceError(environment.projects.BrandRegister, message, data, ex, user);
            return;
        }

        this._insightsService.traceInfo(environment.projects.BrandRegister, message, data, user);
    }

}
